'use client';

import Script from 'next/script';
import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CustomCheckbox } from '../components/forms/CustomCheckbox';

export default function CompliancePage() {
  const [headcount, setHeadcount] = useState<number>(100);
  const [rate, setRate] = useState<number>(1.2);
  const [cost, setCost] = useState<number>(200000);
  const [reduction, setReduction] = useState<number>(60);

  const { incidents, baseline, withInterim, saved, score } = useMemo(() => {
    const incidents = Math.max(0, Math.round((headcount * rate) / 100));
    const baseline = incidents * cost;
    const withInterim = Math.round(baseline * (1 - reduction / 100));
    const saved = baseline - withInterim;
    const score = Math.min(100, Math.max(0, Math.round(reduction)));
    return { incidents, baseline, withInterim, saved, score };
  }, [headcount, rate, cost, reduction]);

  const formatNumber = (num: number) => num.toLocaleString('ru-RU');

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    consent: false,
  });

  const [formStatus, setFormStatus] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type } = e.target;
    const newValue = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;
    setFormData((prev) => ({ ...prev, [name]: newValue }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!formData.consent) {
      setFormStatus('Пожалуйста, дайте согласие на обработку персональных данных.');
      return;
    }

    if (!formData.name || !formData.phone || !formData.email) {
      setFormStatus('Произошла ошибка: имя, телефон и email не могут быть пустыми.');
      return;
    }

    setFormStatus('Отправка...');

    const { consent, ...dataToSend } = formData;

    const apiUrl = `${process.env.NEXT_PUBLIC_STRAPI_API_URL}/api/compliance-requests`;

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ data: dataToSend }),
      });

      if (response.ok) {
        setFormStatus('Ваша заявка успешно отправлена! Скоро мы с вами свяжемся.');
        setFormData({ name: '', phone: '', email: '', consent: false });
      } else {
        const errorData = await response.json();
        const errorMessage = errorData.error?.message || 'Произошла неизвестная ошибка при отправке.';
        setFormStatus(`Произошла ошибка: ${errorMessage}.`);
        console.error('Ошибка:', errorData);
      }
    } catch (error) {
      setFormStatus('Ошибка сети. Пожалуйста, проверьте ваше подключение.');
      console.error('Ошибка:', error);
    }
  };

  return (
    <main className="min-h-screen bg-white text-[#0f172a] font-[Inter,system-ui,-apple-system,Segoe UI,Roboto,Arial,sans-serif] pt-16">
      <Script
        id="jsonld-compliance"
        type="application/ld+json"
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Service',
            name: 'Соблюдение трудового законодательства',
            provider: { '@type': 'Organization', name: 'INTERIM' },
            areaServed: 'RU',
            serviceType: 'Labour Law Compliance & OHS',
          }),
        }}
      />

      <section
        className="relative flex flex-col lg:flex-row items-center min-h-[57vh] text-white overflow-hidden pt-[20px]"
        style={{
          background: `radial-gradient(900px 380px at 15% -10%, #2e5ec5 0%, rgba(46,94,197,.6) 30%, transparent 60%), radial-gradient(1200px 500px at 90% 10%, #0b1f56 0%, #071b42 60%, #05142c 100%)`,
        }}
      >
        <div className="flex-1 px-4 py-8 lg:px-[45px] lg:py-[64px]">
          <div className="max-w-[1200px] mx-auto">
            <span className="inline-flex items-center gap-2 bg-[rgba(255,255,255,.12)] border border-[rgba(255,255,255,.35)] text-white px-3 py-1 rounded-full text-xs">
              Направление: безопасность
            </span>
            <h1 className="text-[34px] leading-tight font-extrabold mt-2 mb-3 md:text-[44px]">
              Соблюдение трудового законодательства
            </h1>
            <p className="text-base text-[#e5e7eb] max-w-[720px]">
              Договоры предоставления персонала (ПП), учёт направлений, электронный документооборот (ЭДО), охрана труда и расследование несчастных случаев. Работаем строго в правовом поле и исключаем риски «заёмного труда».
            </p>
            <div className="mt-4 flex flex-col gap-3 sm:flex-row">
              <Button asChild className="w-full sm:w-auto inline-block bg-[#FF7C22] text-white py-3 px-5 rounded-xl font-bold transition-all duration-200 ease-in-out hover:brightness-[105%] hover:-translate-y-[1px] text-base hover:text-white hover:bg-[#FF7C22]">
                <a href="#risk" className="flex items-center justify-center h-full">Оценить риски</a>
              </Button>
              <Button asChild className="w-full sm:w-auto inline-block bg-white text-[#003B9B] border-2 border-[#003B9B] py-3 px-5 rounded-xl font-bold transition-all duration-200 ease-in-out hover:brightness-[105%] hover:-translate-y-[1px] text-base hover:text-[#003B9B] hover:bg-white">
                <a href="/pricing" className="flex items-center justify-center h-full">Тарифы</a>
              </Button>
            </div>
          </div>
        </div>
        <div className="flex-1 px-4 py-8 lg:px-[32px] lg:py-[64px] flex items-center justify-center">
          <div className="max-w-[1200px] mx-auto w-full">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-white rounded-xl p-4 text-center">
                <div className="text-3xl font-extrabold text-[#003B9B] sm:text-[30px]">100%</div>
                <div className="text-[#64748b] text-sm mt-1">комплаенс процедур</div>
              </div>
              <div className="bg-white rounded-xl p-4 text-center">
                <div className="text-3xl font-extrabold text-[#003B9B] sm:text-[30px]">≤24 ч</div>
                <div className="text-[#64748b] text-sm mt-1">реакция на инцидент</div>
              </div>
              <div className="bg-white rounded-xl p-4 text-center">
                <div className="text-3xl font-extrabold text-[#003B9B] sm:text-[30px]">0</div>
                <div className="text-[#64748b] text-sm mt-1">критических нарушений</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="risk" className="bg-[#f6f8ff] py-10">
        <div className="mx-auto grid max-w-6xl grid-cols-1 gap-6 px-6 py-4 md:grid-cols-2">
          <Card className="rounded-[16px] p-0 shadow-[0_10px_28px_rgba(2,6,23,.08)] border border-[#e5e7eb]">
            <CardContent className="pt-6 px-4 pb-6 md:pt-8 md:px-6 md:pb-8 h-full flex flex-col">
              <h3 className="text-xl font-bold">Калькулятор рисков</h3>
              <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <Label className="block text-xs text-[#64748b] mb-[4px]">Штат, чел.</Label>
                  <Input
                    type="number"
                    value={headcount}
                    onChange={(e) => setHeadcount(Number(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-[#d1d5db] rounded-lg text-sm"
                  />
                </div>
                <div>
                  <Label className="block text-xs text-[#64748b] mb-[4px]">Инцидентов на 100 чел./год</Label>
                  <Input
                    type="number"
                    value={rate}
                    onChange={(e) => setRate(Number(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-[#d1d5db] rounded-lg text-sm"
                  />
                </div>
                <div>
                  <Label className="block text-xs text-[#64748b] mb-[4px]">Средний штраф/ущерб (₽)</Label>
                  <Input
                    type="number"
                    value={cost}
                    onChange={(e) => setCost(Number(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-[#d1d5db] rounded-lg text-sm"
                  />
                </div>
                <div>
                  <Label className="block text-xs text-[#64748b] mb-[4px]">Снижение риска с INTERIM (%)</Label>
                  <Input
                    type="number"
                    value={reduction}
                    onChange={(e) => setReduction(Number(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-[#d1d5db] rounded-lg text-sm"
                  />
                </div>
              </div>
              <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
                <Card className="rounded-xl p-0 shadow-sm border border-[#e5e7eb]">
                  <CardContent className="p-6 text-center">
                    <div className="text-lg text-gray-500">Ожидаемых инцидентов</div>
                    <div className="text-2xl font-extrabold text-[#003B9B] mt-2">{formatNumber(incidents)}</div>
                  </CardContent>
                </Card>
                <Card className="rounded-xl p-0 shadow-sm border border-[#e5e7eb]">
                  <CardContent className="p-6 text-center">
                    <div className="text-lg text-gray-500">Потери без комплаенса/год</div>
                    <div className="text-2xl font-extrabold text-[#003B9B] mt-2">{formatNumber(baseline)}</div>
                  </CardContent>
                </Card>
                <Card className="rounded-xl p-0 shadow-sm border border-[#e5e7eb]">
                  <CardContent className="p-6 text-center">
                    <div className="text-lg text-gray-500">С INTERIM / сэкономлено</div>
                    <div className="text-xl font-extrabold text-[#003B9B] mt-2">{formatNumber(withInterim)} / {formatNumber(saved)}</div>
                  </CardContent>
                </Card>
              </div>
              <div className="mt-4">
                <div className="text-xs text-gray-500">Индекс комплаенса</div>
                <div className="h-3 w-full rounded-full bg-gray-200">
                  <div className="h-3 rounded-full bg-green-500" style={{ width: `${score}%` }} />
                </div>
                <div className="mt-1 text-xs text-gray-600"><strong>{score}%</strong> — выше индекс, ниже риск</div>
              </div>
              <p className="mt-2 text-xs text-gray-500 grow">Оценка ориентировочная и не заменяет юридическую консультацию.</p>
            </CardContent>
          </Card>

          <Card className="rounded-[16px] p-0 shadow-[0_10px_28px_rgba(2,6,23,.08)] border border-[#e5e7eb] h-full flex flex-col">
            <CardContent className="pt-6 px-4 pb-6 md:pt-8 md:px-6 md:pb-12 h-full flex flex-col">
              <h3 className="text-xl font-bold">Пакет юридической надёжности</h3>
              <ul className="mt-2 space-y-2 text-base">
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>
                  <span>Договор о предоставлении труда работников (ПП) + приложения.</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>
                  <span>Журнал учёта направлений, табели, акты, отчёты (ЭДО).</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>
                  <span>Трудовой договор (ТД) и Дополнительное соглашение (ДС) с условиями площадки, оплатой и переработок.</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>
                  <span>Инструктажи, средства индвивидуальной защиты (СИЗ), медосмотры, допуски; обучение безопасным методам.</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>
                  <span>Порядок расследования несчастных случаев совместно с принимающей стороной.</span>
                </li>
              </ul>
              <div className="mt-4">
                <Button
                  asChild
                  className="w-fit bg-[#FF7C22] text-white py-3 px-5 rounded-xl font-bold transition-all duration-200 ease-in-out hover:brightness-[105%] hover:-translate-y-[1px] text-base hover:text-white hover:bg-[#FF7C22]"
                >
                  <a href="/contacts" className="flex items-center justify-center h-full">Запросить образцы</a>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      <section className="py-10 md:py-12">
        <div className="mx-auto grid max-w-6xl grid-cols-1 gap-6 px-6 md:grid-cols-3">
          {[
            { h: 'Правовая чистота', p: 'Работа в рамках российского трудового права. Исключаем «заёмный труд», контролируем основания и сроки направления.' },
            { h: 'Документооборот', p: 'Электронный документооборот (ЭДО), учёт направлений и табелирование, отчётность для проверок и аудит‑трейл.' },
            { h: 'Охрана труда', p: 'Инструктажи, СИЗ, Специальная оценка условий труда (СОУТ), медосмотры и совместное расследование несчастных случаев.' },
          ].map((b, i) => (
            <Card key={i} className="rounded-[16px] p-0 shadow-[0_10px_28px_rgba(2,6,23,.08)] border border-[#e5e7eb]">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold">{b.h}</h3>
                <p className="mt-2 text-base text-gray-600">{b.p}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      <section className="bg-[#f6f8ff] py-10 md:py-12">
        <div className="mx-auto grid max-w-6xl grid-cols-1 gap-6 px-6 py-6 md:grid-cols-2">
          <Card className="rounded-[16px] p-0 shadow-[0_10px_28px_rgba(2,6,23,.08)] border border-[#e5e7eb]">
            <CardContent className="p-6">
              <h3 className="text-xl font-bold">Процесс за 30 дней</h3>
              <ol className="mt-2 space-y-2 text-base text-black">
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>
                  <span>Аудит: процессы, риски, документы</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>
                  <span>Пакет ПП, шаблоны и регламенты</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>
                  <span>Обучение и инструктажи</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>
                  <span>Пилот на площадке и корректировки</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>
                  <span>Масштабирование и KPI‑ревью</span>
                </li>
              </ol>
            </CardContent>
          </Card>

          <Card className="rounded-[16px] p-0 shadow-[0_10px_28px_rgba(2,6,23,.08)] border border-[#e5e7eb]">
            <CardContent className="p-6">
              <h3 className="text-xl font-bold">Кейс: производство</h3>
              <p className="mt-2 text-base text-black">
                Нулевые претензии по переработкам за год, три проверки закрыты без штрафов, цифровой архив документов в ЭДО.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      <section className="py-10 md:py-12">
        <div className="mx-auto grid max-w-6xl grid-cols-1 gap-6 px-6 md:grid-cols-2">
          <Card className="rounded-[16px] border border-[#e5e7eb] shadow-[0_10px_28px_rgba(2,6,23,.08)]">
            <CardContent className="p-6">
              <h3 className="text-xl font-semibold mb-4">FAQ (кратко)</h3>
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-gray-900">Когда требуется учёт мнения профсоюза?</h4>
                  <p className="mt-1 text-sm text-gray-600">
                    При значительном объёме временного привлечения — согласовываем при необходимости.
                  </p>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Где использование временного персонала недопустимо?</h4>
                  <p className="mt-1 text-sm text-gray-600">
                    Опасные объекты и вредные условия — исключаем.
                  </p>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">Кто расследует несчастный случай?</h4>
                  <p className="mt-1 text-sm text-gray-600">
                    Комиссия принимающей стороны с участием представителя INTERIM.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-[16px] p-[20px] shadow-[0_10px_28px_rgba(2,6,23,.08)] border border-[#e5e7eb]">
            <h3 className="text-xl font-semibold">Готовы обсудить?</h3>
            <p className="text-[#64748b] text-base">Оставьте контакты — подготовим предложение и чек‑лист подключения.</p>
            <form onSubmit={handleSubmit}>
              <div className="flex flex-col sm:flex-row gap-[20px] mt-[8px] flex-wrap">
                <div className="flex-1">
                  <Label htmlFor="contactName" className="block text-[12px] text-[#64748b] mb-[6px]">Имя</Label>
                  <Input
                    id="contactName"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Как к вам обращаться"
                    className="w-full px-[12px] py-[10px] border border-[#d1d5db] rounded-[10px]"
                  />
                </div>
                <div className="flex-1">
                  <Label htmlFor="contactPhone" className="block text-[12px] text-[#64748b] mb-[6px]">Телефон</Label>
                  <Input
                    id="contactPhone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="+7 (___) ___‑__‑__"
                    className="w-full px-[12px] py-[10px] border border-[#d1d5db] rounded-[10px]"
                  />
                </div>
                <div className="flex-1">
                  <Label htmlFor="contactEmail" className="block text-[12px] text-[#64748b] mb-[6px]">E-mail</Label>
                  <Input
                    id="contactEmail"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="work@company.com"
                    className="w-full px-[12px] py-[10px] border border-[#d1d5db] rounded-[10px]"
                  />
                </div>
              </div>
              <div className="mt-4">
                <CustomCheckbox
                  name="consent"
                  isChecked={formData.consent}
                  onChange={handleInputChange}
                />
              </div>

              <div className="mt-6">
                <button
                  type="submit"
                  className="bg-[#FF7C22] hover:bg-orange-700 text-white rounded-xl font-bold py-3 px-5 transition-all duration-200 ease-in-out transform hover:-translate-y-0.5"
                >
                  Отправить заявку
                </button>
              </div>
            </form>
            {formStatus && (
              <p className={`mt-2 text-sm ${formStatus.startsWith('Произошла ошибка') || formStatus.includes('сети')
                ? 'text-red-500'
                : formStatus.includes('Пожалуйста')
                  ? 'text-amber-600'
                  : 'text-green-600'
                }`}>
                {formStatus}
              </p>
            )}
          </Card>
        </div>
      </section>
    </main>
  );
}